/**
 * DashboardLayout.tsx — оболочка кабинета (WEB-3739 юнит 2, эталон
 * `design/cabinet-rescin/app.jsx` и `sections/shell-x.jsx`): боковое меню
 * (`Sidebar`, со своим местом `.qm-ph`) и столбец страницы — телефонная капсула
 * (`MobileTopBar`, видна до 640) и `main`. Шапки нет; прокручивается вся
 * страница, конструктор — во весь экран (`main.full`). Плашка заморозки и
 * баннер «нет проектов» — внутри `main`, перед страницей. Отступы `main` — в
 * `styles/cabinet-shell.css` (правило `.ds.mob-host ~ .main`).
 *
 * WEB-3782: прочие плашки каркаса (долг, идущая генерация, поломка доставки в
 * аналитику, старый адрес) сняты — они строки блока «Требует внимания» на
 * главной и число у «Главной» в меню; список держит `ProjectAttentionProvider`
 * над меню и страницей (почему — его докблок).
 *
 * WEB-3310 юнит 8: содержимое `main` (страницы, что пока на antd) — во вложенной
 * теме antd кабинета `buildCabinetTheme` (кит фронтендера 01 §15, подключение
 * 01:540) с выключенной волной нажатия; тема — от пользователя тем же хуком, что
 * у корней `.ds` меню и капсулы. Вне `main` (меню, палитра, окно тарифов) и вне
 * каркаса (вход, публичные страницы, админ-панель) — прежняя `buildAppTheme`.
 *
 * WEB-3310 «слои в кабинете»: служебные слои кита — подсказки `data-tip`
 * (`TipLayer`), телефон (`PhoneRules`), ввод (`TouchRules`), туман лент
 * (`FogRules`) — ставятся здесь один раз, вне `main` и его темы, с `everyRoot`:
 * общего корня `.ds` в кабинете нет, каждый судит узлы с ближайшим `.ds` (меню,
 * капсула, переведённые страницы, конструктор), страницы antd без `.ds` не
 * трогает. Других мест монтажа в кабинете нет (сторож —
 * `DashboardLayout.kitLayers.test.tsx`); витрина `/kit` ставит свои в корне.
 *
 * WEB-4089: права выбранного проекта (`my-role`) грузит оболочка — хук
 * `useShellProjectRights`, один запрос на каждую смену проекта; страницы их
 * сами не спрашивают (сторож `projectRightsDoor.web4089.guard.test.ts`).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { FogRules, PhoneRules, TipLayer, TouchRules } from '@/components/kit/ds';
// WEB-3739 юнит 2: меню нового вида вместо авто-скрываемого (WEB-447) и шапки.
import { Sidebar } from '@/components/layout/Sidebar';
import { MobileTopBar, useShellDrawer } from '@/components/layout/MobileTopBar';
// WEB-3739 юнит 1: палитра ⌘K и баланс кошелька живут в оболочке — с любого
// экрана кабинета, а не внутри закрытого меню профиля.
import { CommandPalette } from '@/components/layout/CommandPalette';
import { useShellBalance } from '@/components/layout/useShellBalance';
// WEB-4089: права выбранного проекта грузит оболочка, один раз на проект.
import { useShellProjectRights } from '@/components/layout/useShellProjectRights';
// WEB-3796: узел всплывашки итога смены организации — у оболочки, вне шторки
// бокового меню (на телефоне она закрывается с visibility: hidden).
import { ShellToastHost } from '@/components/layout/shellToast';
// WEB-754: заморозка проекта по спорному платежу закрывает ВЕСЬ проект
// (сторож статуса отбивает и чтение, и запись) — объяснение обязано жить в
// каркасе кабинета, а не на одном экране.
import ProjectFrozenBanner from '@/components/billing/ProjectFrozenBanner';
// WEB-2413 п.9: строка Amplitude в настройках сообщает «подключение
// сохранено/проверено» — поставщик сигнала обязан стоять над ней и над списком
// «Требует внимания» (WEB-3782: список перечитывается по сигналу).
import { AmplitudeHealthSignalProvider } from '@/components/analytics-integrations/amplitudeHealthSignal';
// WEB-3782: один список «Требует внимания» на проект — для блока главной и
// числа у «Главной» в меню (плашки каркаса → блок + число, решение владельца
// 23.10 и 24.10 6а–6в).
import { ProjectAttentionProvider } from '@/components/attention/ProjectAttentionProvider';
import { OrgBootstrapGate } from '@/components/organizations/OrgBootstrapGate';
// WEB-518: explicit "no projects" empty-state (replaces silent auto-create).
import { ProjectsEmptyBanner } from '@/components/projects/ProjectsEmptyBanner';
import TrialAsk from '@/components/billing/TrialAsk';
import { useAppDispatch, useAppSelector } from '@/hooks/hooks';
import { clearBillingPrompt } from '@/store/billingSlice';
import { buildCabinetTheme } from '@/theme/cabinetAntdTheme';
import { useResolvedTheme } from '@/theme/useResolvedTheme';
import { isEditorRoute } from './editorRoute';

/** Кит 01:540: у antd при нажатии бежит волна, в макете её нет. */
const NO_WAVE = { disabled: true } as const;


export function DashboardLayout() {
  const location = useLocation();
  const dispatch = useAppDispatch();
  // PM decision: every 402 TIER_LIMIT hit (quiz/paywall/project create, AI
  // generation, save/publish) dispatches setBillingPrompt — ONE TrialAsk
  // rendered here shows the same upgrade upsell everywhere instead of each
  // page owning its own modal instance.
  const billingPrompt = useAppSelector(
    (state) => state.billing?.billingPrompt ?? null,
  );
  // WEB-3739 юнит 2: шторка меню на телефоне (закрывается любым переходом).
  const [drawer, setDrawer] = useShellDrawer();
  // WEB-3739: ⌘K / Ctrl+K открывают и закрывают палитру (KeyK — и в русской
  // раскладке, где буква клавиши «л»).
  const [paletteOpen, setPaletteOpen] = useState(false);
  // WEB-4117: при закрытом шлагбауме организаций палитры нет — ⌘K молчит и не
  // копит «открыто» до входа в кабинет.
  const gateBlockingRef = useRef(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (gateBlockingRef.current) return;
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key.toLowerCase() !== 'k' && e.code !== 'KeyK') return;
      e.preventDefault();
      setPaletteOpen((v) => !v);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  // WEB-3739: первая загрузка и автообновление кошелька (WEB-936 п.5).
  useShellBalance();
  useShellProjectRights();
  // WEB-3310 юнит 8: тема пользователя — тот же путь, что у корня `.ds`
  // (useCabinetRootAttributes → useResolvedTheme), шрам WEB-3756.
  const user = useAppSelector((state) => state.auth.user);
  const mode = useResolvedTheme(user?.theme);
  const pageTheme = useMemo(() => buildCabinetTheme(mode), [mode]);
  // WEB-4117 (сверка вида 31.10, мелочь 1): «нет организаций» и «не
  // загрузились» — во весь экран, кабинета за окном нет (эталон app.jsx:633–643):
  // шлагбаум закрыт — без бокового меню, капсулы и палитры ⌘K.
  const [gateBlocking, setGateBlocking] = useState(false);
  gateBlockingRef.current = gateBlocking;

  return (
    <AmplitudeHealthSignalProvider>
      <ProjectAttentionProvider>
        <ShellToastHost>
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            {!gateBlocking && (
              <Sidebar
                drawer={drawer}
                onClose={() => setDrawer(false)}
                onFind={() => setPaletteOpen(true)}
              />
            )}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              {!gateBlocking && (
                <MobileTopBar
                  open={drawer}
                  onToggle={() => setDrawer(!drawer)}
                  onClose={() => setDrawer(false)}
                />
              )}
              {/* WEB-467 п.2/п.3: конструктор — приложение во весь экран, без
                отступов и без прокрутки страницы (высота — правило main.full). */}
              <main
                data-testid="page"
                className={'main' + (isEditorRoute(location.pathname) ? ' full' : '')}
              >
                <ConfigProvider theme={pageTheme} wave={NO_WAVE}>
                  <ProjectFrozenBanner />
                  <OrgBootstrapGate onBlockingChange={setGateBlocking}>
                    <ProjectsEmptyBanner />
                    <div className="cab-page">
                      <Outlet />
                    </div>
                  </OrgBootstrapGate>
                </ConfigProvider>
              </main>
            </div>
            {/* WEB-1908 (возврат приёмки В11-Л6): окно открывается по денежному
              отказу — вместе с ним едет ПРИЧИНА (`PAYMENT_REQUIRED_*`), и окно
              говорит человеку, что именно упёрлось, вместо голого «Выберите
              тариф». Причины нет (окно подняли клиентским гейтом, без ответа
              сервера) — строки просто не будет. */}
            {/* WEB-4180: окно «Начните пробный период» вместо окна выбора тарифа;
              счёта «Использовано N из M» в окне нет (решение владельца 03.11 «рек»,
              п. 1) — usage/limit остаются в billingPrompt, окну не передаются. */}
            {!gateBlocking && (
              <CommandPalette
                open={paletteOpen}
                onClose={() => setPaletteOpen(false)}
              />
            )}
            <TrialAsk
              open={billingPrompt !== null}
              reasonCode={billingPrompt?.reasonCode}
              onClose={() => dispatch(clearBillingPrompt())}
            />
            <TipLayer everyRoot />
            <TouchRules everyRoot />
            <FogRules everyRoot />
            <PhoneRules everyRoot />
          </div>
        </ShellToastHost>
      </ProjectAttentionProvider>
    </AmplitudeHealthSignalProvider>
  );
}
